
import React, { useState, useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { AdminRouter } from './AdminRouter';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsAndConditions } from './components/TermsAndConditions';
import { LegalDisclaimer } from './components/LegalDisclaimer';
import { UnifiedAuthPageClean as UnifiedAuthPage } from './components/UnifiedAuthPageClean';
import { UserDashboard } from './components/UserDashboard'
import { AffiliateDashboard } from './components/AffiliateDashboard';
import { AccountSuspended } from './components/AccountSuspended';
import { initializeReferralTracking } from './lib/referralPersistence';
import { getCurrentUser, getCurrentUserToken, signOut, clearAllUserSessionData } from './lib/supabase';
import { createServerSession, getSessionHeaders, hasServerSession } from './lib/serverSession';
// SECURITY: Email processing moved to server-side (api/cron/process-email-queue.js)
// Client-side processing removed to prevent PII exposure
import AffiliateLandingPage from './components/AffiliateLandingPage'
import { FlowingLandingPage as CustomAffiliateLandingPage } from './components/affiliate/FlowingLandingPage';
import CorporateHomepage from './CorporateHomepage';
import PurchaseSharesPage from './pages/PurchaseSharesPage';
import { GoldDiggersDashboard } from './components/GoldDiggersDashboard';
import WithdrawalVerifiedPage from './pages/WithdrawalVerifiedPage';
import AdminLoginVerifiedPage from './pages/AdminLoginVerifiedPage';
import { NewsTickerBar } from './components/NewsTickerBar';
import { AnnouncementPopup } from './components/user/AnnouncementPopup';
import { PreferredLanguagePrompt } from './components/user/PreferredLanguagePrompt';
import { isBirthdayPromoActive } from './constants';
import { MaintenanceModePage } from './components/MaintenanceModePage';
import { MaintenanceWarningBanner } from './components/MaintenanceWarningBanner';
import { DIVIDENDS_USER_VISIBLE } from './lib/dividendsUserVisible';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import ShareAuditPage from './components/ShareAuditPage';
import VerifyEmailChangePage from './pages/verify-email-change';


const App: React.FC = () => {
    const [showAdmin, setShowAdmin] = useState(false);
    const [currentSection, setCurrentSection] = useState<string>('home');
    const [user, setUser] = useState<any>(null);
    const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [previousSection, setPreviousSection] = useState<string>('home'); // Track where user came from
    const [activeDashboard, setActiveDashboard] = useState<'shareholder' | 'affiliate'>('shareholder');
    const [affiliateUsername, setAffiliateUsername] = useState<string>('');
    const [isAdminUser, setIsAdminUser] = useState(false);

    // Top offset below the fixed ticker. The exact px value is defined once in
    // aureus.css as the --app-top-offset / --app-warning-offset CSS variables,
    // which adapt responsively (desktop vs mobile) and to the birthday promo via
    // the .birthday-promo-on root class (toggled in the effect below). Content
    // wrappers reference these variables so a single edit keeps every surface in
    // sync. Auto-reverts after the promo window (isBirthdayPromoActive() -> false).
    const birthdayActive = isBirthdayPromoActive();
    const baseTopOffset = 'var(--app-top-offset)';
    const warningTopOffset = 'var(--app-warning-offset)';

    // Toggle the promo class on the document root so the CSS variables above
    // switch to the taller banner-inclusive offsets while the promo is active.
    useEffect(() => {
        const root = document.documentElement;
        if (birthdayActive) {
            root.classList.add('birthday-promo-on');
        } else {
            root.classList.remove('birthday-promo-on');
        }
        return () => { root.classList.remove('birthday-promo-on'); };
    }, [birthdayActive]);

    // Check if current user is an admin (via admin_users table, through my-shares API)
    const checkAdminStatus = async (userId: number): Promise<boolean> => {
        try {
            const res = await fetch('/api/user/my-shares', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                body: JSON.stringify({ userId })
            });
            const data = await res.json();
            const admin = !!data.isAdmin;
            setIsAdminUser(admin);
            return admin;
        } catch {
            setIsAdminUser(false);
            return false;
        }
    };

    // Check maintenance mode status
    const { status: maintenanceStatus } = useMaintenanceMode();

    // Scroll to top whenever currentSection changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentSection]);

    // Handle direct URL access to login/register pages - both go to unified account access page
    useEffect(() => {
        const path = window.location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        console.log('🔄 URL routing useEffect triggered', { path, currentSection, isMobile });

        // Initialize referral tracking for all page loads
        initializeReferralTracking();

        // Handle payment query parameters on root path (/?payment=success, /?payment=error, etc.)
        const paymentStatus = urlParams.get('payment');
        if (path === '/' && paymentStatus) {
            console.log('💳 Payment status query parameter detected:', paymentStatus);

            // Store payment details for the success modal
            if (paymentStatus === 'success') {
                const paymentDetails = {
                    status: 'success',
                    shares: urlParams.get('shares'),
                    amount: urlParams.get('amount'),
                    reference: urlParams.get('reference'),
                    method: urlParams.get('method')
                };
                localStorage.setItem('aureus_payment_result', JSON.stringify(paymentDetails));
                console.log('💳 Payment success details stored:', paymentDetails);
            } else {
                const paymentDetails = {
                    status: paymentStatus,
                    reason: urlParams.get('reason')
                };
                localStorage.setItem('aureus_payment_result', JSON.stringify(paymentDetails));
                console.log('💳 Payment result stored:', paymentDetails);
            }

            // Redirect to dashboard where the payment modal will be shown
            setCurrentSection('dashboard');
            window.history.pushState({}, '', '/dashboard');
            return;
        }

        if (path === '/testlogin') {
            console.log('🔐 Processing /testlogin URL (maintenance bypass for admin users)');
            setCurrentSection('test-login');
            setAuthMode('login');
        } else if (path === '/login' || path === '/register') {
            console.log('🔐 Processing login/register URL', { path, currentSection });
            // Check if we're in the middle of an authentication success redirect
            const authSuccessRedirect = localStorage.getItem('aureus_auth_success_redirect');
            if (authSuccessRedirect === 'true') {
                console.log('🔄 Authentication success redirect in progress, skipping login page setup');
                return;
            }

            // Check if user is already authenticated (mobile fix) - but validate the session first
            const existingUser = localStorage.getItem('aureus_user');
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const mobileAuthInProgress = localStorage.getItem('aureus_mobile_auth_redirect') === 'true';

            // Only redirect if we have valid user data AND the user is actually authenticated
            if (existingUser && isMobile && !mobileAuthInProgress) {
                try {
                    const userData = JSON.parse(existingUser);
                    // Validate that the user data has essential fields and isn't stale
                    const hasValidData = userData && userData.id && userData.email;
                    const authTimestamp = localStorage.getItem('aureus_auth_timestamp');
                    const isRecentAuth = authTimestamp && (Date.now() - parseInt(authTimestamp)) < 24 * 60 * 60 * 1000; // 24 hours

                    if (hasValidData && isRecentAuth) {
                        console.log('📱 Mobile user has valid recent authentication, redirecting to dashboard');
                        setCurrentSection('dashboard');
                        window.history.pushState({}, '', '/dashboard');
                        return;
                    } else {
                        console.log('📱 Mobile user has stale/invalid authentication data, allowing login page access');
                        // Clear stale data
                        localStorage.removeItem('aureus_user');
                        localStorage.removeItem('aureus_auth_timestamp');
                        localStorage.removeItem('aureus_mobile_auth_redirect'); // Clear this flag too
                    }
                } catch (error) {
                    console.log('📱 Invalid user data in localStorage, clearing and allowing login');
                    localStorage.removeItem('aureus_user');
                    localStorage.removeItem('aureus_mobile_auth_redirect'); // Clear this flag too
                }
            }

            // REMOVED: The problematic early return that was blocking login page access
            // Mobile auth redirect flag should not prevent login page access

            console.log('🔐 Setting currentSection to account-access from useEffect');
            setCurrentSection('account-access');
            // Set authMode based on the original URL for context
            setAuthMode(path === '/register' ? 'register' : 'login');
        } else if (path === '/purchase-shares') {
            // Check if user is authenticated for purchase shares page
            getCurrentUser().then(currentUser => {
                if (currentUser) {
                    console.log('✅ User authenticated, showing purchase shares page');
                    setCurrentSection('purchase-shares');
                } else {
                    console.log('🔐 User not authenticated, redirecting to login with purchase intent');
                    localStorage.setItem('aureus_post_login_redirect', '/purchase-shares');
                    setCurrentSection('account-access');
                    setAuthMode('login');
                    window.history.pushState({}, '', '/login');
                }
            }).catch(error => {
                console.error('❌ Error checking user authentication:', error);
                // Fallback to login page
                localStorage.setItem('aureus_post_login_redirect', '/purchase-shares');
                setCurrentSection('account-access');
                setAuthMode('login');
                window.history.pushState({}, '', '/login');
            });
        } else if (path === '/gold-diggers') {
            getCurrentUser().then(currentUser => {
                if (currentUser) {
                    setCurrentSection('gold-diggers');
                } else {
                    localStorage.setItem('aureus_post_login_redirect', '/gold-diggers');
                    setCurrentSection('account-access');
                    setAuthMode('login');
                    window.history.pushState({}, '', '/login');
                }
            }).catch(() => {
                localStorage.setItem('aureus_post_login_redirect', '/gold-diggers');
                setCurrentSection('account-access');
                setAuthMode('login');
                window.history.pushState({}, '', '/login');
            });
        } else if (path === '/reset-password') {
            // Redirect to login page - password reset is now handled within UnifiedAuthPage
            setCurrentSection('account-access');
            setAuthMode('login');
            window.history.pushState({}, '', '/login');
        } else if (path === '/sync') {
            // Handle sync page with token
            const syncToken = urlParams.get('token');
            if (syncToken) {
                setCurrentSection('sync');
                // Store sync token for the sync page
                (window as any).syncToken = syncToken;
            } else {
                // No token, redirect to home
                setCurrentSection('home');
                window.history.pushState({}, '', '/');
            }
        } else if (path === '/affiliate') {
            setCurrentSection('affiliate');
            // Track registration path for user type detection
            localStorage.setItem('aureus_registration_path', '/affiliate');
        } else if (path === '/complete-profile') {
            // Redirect to dashboard - profile completion is now handled within dashboard
            console.log('🔧 Profile completion route detected - redirecting to dashboard');
            setCurrentSection('dashboard');
            window.history.pushState({}, '', '/dashboard');
        } else if (path === '/dashboard') {
            // Handle dashboard route - check if user is logged in
            console.log('🏠 Dashboard route detected');
            // This will be handled by the session check logic

        } else if (path === '/withdrawal-verified') {
            // Withdrawal email link verification result page
            console.log('🔐 Withdrawal verification page detected');
            setCurrentSection('withdrawal-verified');
        } else if (path === '/admin-login-verified') {
            // Admin login email link verification result page
            console.log('🔐 Admin login verification page detected');
            setCurrentSection('admin-login-verified');
        } else if (path === '/verify-email-change') {
            // Email change verification page
            console.log('📧 Email change verification page detected');
            setCurrentSection('verify-email-change');
        } else if (path === '/share-audit') {
            console.log('🔍 Share audit page detected');
            setCurrentSection('share-audit');
        } else if (path === '/privacy-policy') {
            setCurrentSection('privacy-policy');
        } else if (path === '/terms-conditions' || path === '/terms-of-service') {
            setCurrentSection('terms-conditions');
        } else if (path !== '/') {
            // Check if this is a username-based affiliate landing page
            const username = path.substring(1); // Remove leading slash
            console.log('🔍 Checking path for affiliate landing:', { path, username, hasSlash: username.includes('/'), length: username.length, startsWithStatic: username.startsWith('static/') });
            // Allow usernames with dots but not slashes (to avoid matching other routes)
            // Exclude reserved paths like 'dashboard', 'admin', etc.
            const reservedPaths = [
                'dashboard', 'admin', 'api', 'static', '_next', 'favicon.ico',
                'complete-profile', 'login', 'register', 'purchase-shares', 'affiliate',
                'migrate', 'sync', 'reset-password', 'share-audit',
                'privacy-policy', 'terms-conditions', 'terms-of-service', 'kyc-certification', 'aureus.js', 'payment',
                'withdrawal-verified', 'admin-login-verified', 'testlogin', 'gold-diggers',
                'verify-email-change'
            ];
            if (username && !username.includes('/') && username.length > 0 && !username.startsWith('static/') && !reservedPaths.includes(username)) {
                console.log('🎯 Potential affiliate landing page for username:', username);
                setCurrentSection('affiliate-landing');
                setAffiliateUsername(username);
            } else {
                console.log('❌ Path did not match affiliate landing criteria or is reserved path');
            }
        }
    }, []);
    // Global error handler for SVG and other errors
    useEffect(() => {
        const handleGlobalError = (event: ErrorEvent) => {
            // Suppress jQuery SVG path errors that don't affect our app
            if (event.message && event.message.includes('Expected number') && event.message.includes('path')) {
                console.warn('Suppressed SVG path error (likely from browser extension):', event.message);
                event.preventDefault();
                return false;
            }
            // Log other errors normally
            console.error('Global error:', event.error);
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error('Unhandled promise rejection:', event.reason);
        };

        window.addEventListener('error', handleGlobalError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleGlobalError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);




    const handleRegistrationSuccess = async (userData: any) => {
        console.log('✅ Registration successful:', userData);

        // Extract the actual database user data from the registration response
        const dbUser = userData.database_user || userData;

        console.log('🔍 Registration complete - going directly to dashboard (no profile completion step)');

        // Structure user data properly for dashboard compatibility
        const structuredUser = {
            id: `db_${dbUser.id}`,
            email: dbUser.email,
            username: dbUser.username,
            account_type: 'email',
            user_type: 'shareholder', // Default to shareholder for new registrations
            database_user: {
                id: dbUser.id,
                email: dbUser.email,
                username: dbUser.username,
                full_name: dbUser.full_name || '',
                phone: dbUser.phone || '',
                country_of_residence: dbUser.country_of_residence || 'ZA',
                country_selection_completed: true, // Mark as completed to skip profile completion
                password_hash: dbUser.password_hash,
                is_active: true,
                is_verified: false,
                role: 'user',
                account_type: 'email'
            },
            needsProfileCompletion: false // No profile completion step - go directly to dashboard
        };

        console.log('📝 Structured user data:', structuredUser);

        // Store the FLAT database user. Login does the same. A structured wrapper
        // here made getCurrentUser treat db_N as the row and wipe the session on remount
        // (KYC Privacy/Terms <a href> full-page load).
        localStorage.setItem('aureus_user', JSON.stringify(structuredUser.database_user));
        localStorage.setItem('aureus_user_type', 'shareholder');
        localStorage.setItem('aureus_auth_timestamp', Date.now().toString()); // Add auth timestamp

        setUser(structuredUser);

        // Registration already starts the server session with the new sign-in token.
        // A second call without that token is rejected and leaves the previous account's session in place.
        if (!hasServerSession()) {
            try {
                const authToken = await getCurrentUserToken();
                const sessionResult = await createServerSession(
                    dbUser.id,
                    dbUser.email,
                    'email',
                    undefined,
                    undefined,
                    authToken
                );
                if (sessionResult.success) {
                    console.log('✅ Server-side session created for new user:', sessionResult.sessionId?.substring(0, 8) + '...');
                } else {
                    console.warn('⚠️ Failed to create server session for new user:', sessionResult.error);
                }
            } catch (sessionError) {
                console.warn('⚠️ Failed to create server-side session for new user:', sessionError);
            }
        }

        // Use the default dashboard logic to handle routing properly
        handleDefaultDashboardLogic(structuredUser);
    };

    // Handle successful magic link login
    const __handleLoginSuccess = (userData: any) => {
        console.log('✅ Magic link login successful:', userData);

        // Validate user ID - must be a valid numeric ID
        if (!userData.id || (typeof userData.id !== 'number' && isNaN(parseInt(userData.id)))) {
            console.error('❌ Invalid user data - missing or invalid ID:', userData);
            alert('Login failed: Invalid user data. Please try again.');
            return;
        }

        // Ensure ID is a number
        const numericId = typeof userData.id === 'number' ? userData.id : parseInt(userData.id);

        // CRITICAL: Check if userData is already structured (has database_user) or is flat API response
        const isAlreadyStructured = userData.database_user && userData.database_user.full_name;

        let structuredUser: any;

        if (isAlreadyStructured) {
            // Data is already properly structured - use it directly
            console.log('📋 User data already structured, using directly');
            structuredUser = {
                ...userData,
                id: numericId,
                account_type: userData.account_type || 'email'
            };
        } else {
            // Flat API response - structure it for dashboard compatibility
            console.log('📋 Structuring flat user data for dashboard');
            structuredUser = {
                id: numericId,
                email: userData.email,
                username: userData.username,
                account_type: 'email',
                user_type: userData.user_type || 'shareholder',
                database_user: {
                    id: numericId,
                    email: userData.email,
                    username: userData.username,
                    full_name: userData.full_name,
                    phone: userData.phone,
                    country_of_residence: userData.country_of_residence,
                    profile_image_url: userData.profile_image_url,
                    user_type: userData.user_type || 'shareholder',
                    is_active: userData.is_active,
                    auth_user_id: userData.auth_user_id,
                    // v4.7.63 Fraud Freeze: propagate suspension flags into the
                    // client session so the AccountSuspended guard in App.tsx
                    // can lock the UI down on every render.
                    account_status: userData.account_status || 'active',
                    suspended_at: userData.suspended_at || null,
                    suspension_reason: userData.suspension_reason || null
                },
                user_metadata: {
                    user_id: numericId,
                    email: userData.email,
                    username: userData.username,
                    full_name: userData.full_name,
                    profile_image_url: userData.profile_image_url
                }
            };
        }

        console.log('📋 Final structured user for dashboard:', {
            id: structuredUser.id,
            full_name: structuredUser.database_user?.full_name,
            phone: structuredUser.database_user?.phone,
            country_of_residence: structuredUser.database_user?.country_of_residence,
            profile_image_url: structuredUser.database_user?.profile_image_url
        });

        // Store in localStorage
        localStorage.setItem('aureus_user', JSON.stringify(structuredUser));
        localStorage.setItem('aureus_auth_timestamp', Date.now().toString());

        setUser(structuredUser);

        // Use the default dashboard logic to handle routing properly
        handleDefaultDashboardLogic(structuredUser);
    };


    // Mobile Authentication State Manager - Prevents Race Conditions
    const mobileAuthManager = {
        isRedirecting: false,
        redirectAttempts: 0,
        maxAttempts: 3,

        startRedirect: function(userData: any) {
            if (this.isRedirecting) {
                console.log('📱 Mobile redirect already in progress, ignoring duplicate request');
                return;
            }

            this.isRedirecting = true;
            this.redirectAttempts = 0;

            console.log('📱 Starting mobile authentication redirect sequence');

            // Clear any existing redirect flags to prevent conflicts
            localStorage.removeItem('aureus_auth_success_redirect');
            localStorage.removeItem('aureus_post_login_redirect');

            // Set mobile-specific authentication flag
            localStorage.setItem('aureus_mobile_auth_redirect', 'true');
            localStorage.setItem('aureus_auth_timestamp', Date.now().toString());

            // Store user data
            localStorage.setItem('aureus_user', JSON.stringify(userData.database_user));
            localStorage.setItem('aureus_user_type', userData.user_type || 'shareholder');

            // Set user state immediately
            setUser(userData);

            // Perform redirect
            this.performRedirect();
        },

        performRedirect: function() {
            this.redirectAttempts++;

            console.log(`📱 Mobile redirect attempt ${this.redirectAttempts}/${this.maxAttempts}`);

            // Use the default dashboard logic to handle profile completion checks
            let userData = {};
            try {
                const storedUser = localStorage.getItem('aureus_user');
                console.log('📱 Raw stored user data:', storedUser);

                if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
                    userData = JSON.parse(storedUser);
                } else {
                    console.log('📱 No valid user data found in localStorage, using empty object');
                }
            } catch (error) {
                console.error('📱 Error parsing user data from localStorage:', error);
                console.log('📱 Using empty user data object');
            }

            console.log('📱 Using handleDefaultDashboardLogic for mobile redirect');
            handleDefaultDashboardLogic({ database_user: userData });

            // Verify redirect success after a delay
            setTimeout(() => {
                const currentPath = window.location.pathname;
                const currentSectionValue = currentSection;

                console.log(`📱 Redirect verification - Path: ${currentPath}, Section: ${currentSectionValue}`);

                // Accept dashboard, migration pages, or any valid redirect as success
                const validPaths = ['/dashboard', '/migrate'];
                const isValidRedirect = validPaths.some(path => currentPath === path) || currentPath.includes('migrate');

                if (!isValidRedirect && this.redirectAttempts < this.maxAttempts) {
                    console.log('📱 Redirect failed, retrying...');
                    this.performRedirect();
                } else if (isValidRedirect) {
                    console.log('📱 Mobile redirect successful!');
                    this.cleanup();
                } else {
                    console.log('📱 Max redirect attempts reached, forcing final redirect');
                    window.location.href = '/dashboard';
                    this.cleanup();
                }
            }, 1200); // Increased delay for mobile browsers
        },

        cleanup: function() {
            this.isRedirecting = false;
            this.redirectAttempts = 0;
            localStorage.removeItem('aureus_mobile_auth_redirect');
            localStorage.removeItem('aureus_auth_timestamp');
            console.log('📱 Mobile auth manager cleanup complete');
        }
    };

    // Function to refresh user data from database
    const __loadUserData = async () => {
        try {
            console.log('🔄 Refreshing user data from database...');
            const currentUser = await getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
                console.log('✅ User data refreshed successfully');
            }
        } catch (error) {
            console.error('❌ Error refreshing user data:', error);
        }
    };

    const handleLogout = async () => {
        console.log('🚪 handleLogout called (v2.9.74 - unified session cleanup)');
        try {
            // First, clear React state immediately to prevent UI contamination
            setUser(null);
            setCurrentSection('home');
            setActiveDashboard('shareholder'); // Reset to default dashboard

            // Call Supabase signOut (which internally calls clearAllUserSessionData)
            console.log('🔐 Calling signOut...');
            const signOutResult = await signOut();
            console.log('🔐 SignOut result:', signOutResult);

            // SECURITY: Call clearAllUserSessionData AGAIN as a safety net
            // signOut() calls it internally, but we call it again in case signOut() threw
            // before reaching it. clearAllUserSessionData is idempotent.
            clearAllUserSessionData();

            // Clean up mobile auth manager
            mobileAuthManager.cleanup();

            // Set logout flag AFTER clearing (clearAllUserSessionData removes aureus_* keys)
            localStorage.setItem('aureus_logout_flag', 'true');

            // Force navigation to home and reload for complete state reset
            window.history.pushState({}, '', '/');
            console.log('🔄 Forcing page reload to ensure complete logout...');
            setTimeout(() => {
                window.location.reload();
            }, 100);

            console.log('✅ Logged out successfully - all data cleared');
        } catch (error) {
            console.error('❌ Logout error:', error);
            // SECURITY: Even on error, force complete cleanup
            console.log('🧹 Force clearing all data despite error...');

            setUser(null);
            setCurrentSection('home');

            // Use the unified cleanup function (single source of truth)
            clearAllUserSessionData();

            // Clean up mobile auth manager
            try { mobileAuthManager.cleanup(); } catch (_) { /* ignore */ }

            // Set logout flag AFTER clearing
            localStorage.setItem('aureus_logout_flag', 'true');

            // Force navigation and reload
            window.history.pushState({}, '', '/');
            setTimeout(() => {
                window.location.reload();
            }, 100);

            console.log('✅ Force logout completed');
        }
    };

    // Dashboard switching handler
    const handleDashboardSwitch = (dashboard: 'shareholder' | 'affiliate') => {
        setActiveDashboard(dashboard);
        localStorage.setItem('aureus_active_dashboard', dashboard);
        console.log(`🔄 Switched to ${dashboard} dashboard`);
    };







    // Handle default dashboard logic
    const handleDefaultDashboardLogic = (currentUser: any) => {
        console.log('🏠 Executing default dashboard logic for user:', currentUser.email);

        // Mobile-specific debugging
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
            console.log('📱 Mobile device - executing dashboard logic');
            console.log('📱 Current user data:', {
                email: currentUser.email,
                country_completed: currentUser.database_user?.country_selection_completed,
                full_name: currentUser.database_user?.full_name,
                phone: currentUser.database_user?.phone,
                needsProfileCompletion: currentUser.needsProfileCompletion,
                profile_completion_required: currentUser.user_metadata?.profile_completion_required
            });
        }

        // MOBILE FIX: Profile completion is now handled in legal documents page within dashboard
        // For mobile users, automatically clear profile completion flags and go to dashboard
        const needsProfileCompletion = currentUser.needsProfileCompletion ||
                                     currentUser.user_metadata?.profile_completion_required;

        if (needsProfileCompletion && isMobile) {
            console.log('📱 Mobile user needs profile completion - auto-clearing flags and going to dashboard');

            // Auto-clear profile completion flags for mobile users
            const updatedUser = {
                ...currentUser,
                needsProfileCompletion: false,
                user_metadata: {
                    ...currentUser.user_metadata,
                    profile_completion_required: false
                }
            };

            // Update user in state and localStorage
            setUser(updatedUser);
            localStorage.setItem('aureus_user', JSON.stringify(updatedUser));

            console.log('📱 Mobile profile completion flags cleared automatically');
        }

        // Always go to dashboard - profile completion is now handled within dashboard
        console.log('✅ Going to dashboard - profile completion handled in legal documents');
        if (isMobile) console.log('📱 Mobile redirect: dashboard');
        setCurrentSection('dashboard');
        // Update URL without page reload to avoid infinite loop
        if (window.location.pathname !== '/dashboard') {
            window.history.pushState({}, '', '/dashboard');
        }
        // Update URL without page reload to avoid infinite loop
        if (window.location.pathname !== '/dashboard') {
            window.history.pushState({}, '', '/dashboard');
        }
    };

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            try {
                console.log('🔍 Checking for existing session...');
                console.log('🔍 [DEBUG] localStorage aureus_test_user:', localStorage.getItem('aureus_test_user'));

                // AUTO-MIGRATION: Fix old cache format (flat user data → structured with database_user)
                // This ensures existing users don't need to clear their cache manually
                const storedUser = localStorage.getItem('aureus_user');
                if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
                    try {
                        const parsed = JSON.parse(storedUser);
                        // Check if user data is in OLD format (has full_name at root level but no database_user.full_name)
                        if (parsed.full_name && !parsed.database_user?.full_name) {
                            console.log('🔄 AUTO-MIGRATION: Converting old cache format to new structure');
                            const numericId = typeof parsed.id === 'number' ? parsed.id : parseInt(parsed.id);
                            const migratedUser = {
                                id: numericId,
                                email: parsed.email,
                                username: parsed.username,
                                account_type: parsed.account_type || 'email',
                                user_type: parsed.user_type || parsed.role || 'shareholder',
                                database_user: {
                                    id: numericId,
                                    email: parsed.email,
                                    username: parsed.username,
                                    full_name: parsed.full_name,
                                    phone: parsed.phone,
                                    country_of_residence: parsed.country_of_residence,
                                    profile_image_url: parsed.profile_image_url,
                                    user_type: parsed.user_type || parsed.role || 'shareholder',
                                    is_active: parsed.is_active,
                                    auth_user_id: parsed.auth_user_id
                                },
                                user_metadata: {
                                    user_id: numericId,
                                    email: parsed.email,
                                    username: parsed.username,
                                    full_name: parsed.full_name,
                                    profile_image_url: parsed.profile_image_url
                                }
                            };
                            localStorage.setItem('aureus_user', JSON.stringify(migratedUser));
                            console.log('✅ AUTO-MIGRATION: Cache format updated successfully');
                        }
                    } catch (e) {
                        console.warn('⚠️ Failed to check/migrate cache format:', e);
                    }
                }

                // Check if user recently logged out
                const logoutFlag = localStorage.getItem('aureus_logout_flag');
                if (logoutFlag === 'true') {
                    console.log('🚪 User recently logged out, skipping auto-login');
                    localStorage.removeItem('aureus_logout_flag'); // Clear the flag
                    setIsCheckingSession(false);
                    return;
                }

                const currentUser = await getCurrentUser();
                console.log('🔍 [DEBUG] getCurrentUser result:', currentUser);

                if (currentUser) {
                    console.log('✅ Found existing session:', currentUser);
                    console.log('🔍 [DEBUG] currentUser.needsProfileCompletion:', currentUser.needsProfileCompletion);
                    console.log('🔍 [DEBUG] currentUser.user_metadata?.profile_completion_required:', currentUser.user_metadata?.profile_completion_required);

                    setUser(currentUser);

                    // Check admin status for maintenance exemption and admin features
                    const sessionUserId = currentUser.database_user?.id || currentUser.id;
                    if (sessionUserId) {
                        const numId = typeof sessionUserId === 'number' ? sessionUserId : parseInt(String(sessionUserId).replace(/db_/g, ''));
                        if (!isNaN(numId)) {
                            checkAdminStatus(numId).catch(() => {}); // Fire and forget - non-blocking
                        }
                    }

                    // Don't auto-redirect if user is trying to access affiliate page
                    const currentPath = window.location.pathname;
                    console.log('🔍 Current path during session check:', currentPath);

                    if (currentPath === '/affiliate') {
                        console.log('🏢 User accessing affiliate page, staying on affiliate page');
                        setCurrentSection('affiliate');
                    } else if (currentPath === '/login' || currentPath === '/register') {
                        console.log('🔐 User accessing auth page, staying on account-access');
                        setCurrentSection('account-access');
                        setAuthMode(currentPath === '/register' ? 'register' : 'login');
                    } else if (currentPath === '/purchase-shares') {
                        console.log('💰 User accessing purchase-shares page, showing purchase shares');
                        setCurrentSection('purchase-shares');

                    } else if (currentPath === '/dashboard') {
                        console.log('🏠 User accessing dashboard page, redirecting to dashboard');
                        handleDefaultDashboardLogic(currentUser);
                    } else if (currentPath === '/privacy-policy') {
                        setCurrentSection('privacy-policy');
                    } else if (currentPath === '/terms-conditions' || currentPath === '/terms-of-service') {
                        setCurrentSection('terms-conditions');
                    } else if (currentPath.includes('migrate')) {
                        console.log('🔄 User accessing migration page, staying on migration page');
                        // Don't redirect migration pages - let them handle their own logic
                        return;
                    } else if (currentPath !== '/' && !currentPath.includes('/')) {
                        // Check if this is a username-based affiliate landing page
                        const username = currentPath.substring(1); // Remove leading slash
                        const reservedPaths = [
                            'dashboard', 'admin', 'api', 'static', '_next', 'favicon.ico',
                            'complete-profile', 'login', 'register', 'purchase-shares', 'affiliate',
                            'migrate', 'sync', 'reset-password', 'share-audit',
                            'privacy-policy', 'terms-conditions', 'terms-of-service', 'kyc-certification', 'aureus.js', 'testlogin',
                            'verify-email-change', 'gold-diggers'
                        ];
                        if (username && username.length > 0 && !reservedPaths.includes(username)) {
                            console.log('🎯 Username-based affiliate landing page detected during session check:', username);
                            setCurrentSection('affiliate-landing');
                            setAffiliateUsername(username);
                        } else {
                            // Default dashboard logic
                            handleDefaultDashboardLogic(currentUser);
                        }
                    } else {

                        // Always go to dashboard - profile completion is now handled in legal documents page
                        console.log('✅ Going to dashboard - profile completion handled in legal documents');
                        setCurrentSection('dashboard');
                    }
                } else {
                    console.log('ℹ️ No existing session found');

                    // CRITICAL FIX: When no session exists, still respect the URL routing
                    // This ensures /login and /register pages are shown even without a session
                    const currentPath = window.location.pathname;
                    if (currentPath === '/login' || currentPath === '/register') {
                        console.log('🔐 No session but user on auth page, setting account-access');
                        setCurrentSection('account-access');
                        setAuthMode(currentPath === '/register' ? 'register' : 'login');
                    } else if (currentPath === '/affiliate') {
                        console.log('🏢 No session but user on affiliate page');
                        setCurrentSection('affiliate');
                    } else if (currentPath === '/purchase-shares') {
                        console.log('💰 No session but user trying to access purchase-shares, redirecting to login');
                        localStorage.setItem('aureus_post_login_redirect', '/purchase-shares');
                        setCurrentSection('account-access');
                        setAuthMode('login');
                        window.history.pushState({}, '', '/login');
                    } else if (currentPath === '/privacy-policy') {
                        setCurrentSection('privacy-policy');
                    } else if (currentPath === '/terms-conditions' || currentPath === '/terms-of-service') {
                        setCurrentSection('terms-conditions');
                    } else if (currentPath !== '/' && !currentPath.startsWith('/static') && !currentPath.startsWith('/_next')) {
                        // Check if this is a username-based affiliate landing page
                        const username = currentPath.substring(1); // Remove leading slash
                        const reservedPaths = [
                            'dashboard', 'admin', 'api', 'static', '_next', 'favicon.ico',
                            'complete-profile', 'login', 'register', 'purchase-shares', 'affiliate',
                            'migrate', 'sync', 'reset-password', 'share-audit', 'payment',
                            'privacy-policy', 'terms-conditions', 'terms-of-service', 'kyc-certification', 'aureus.js',
                            'withdrawal-verified', 'admin-login-verified', 'testlogin', 'gold-diggers',
                            'verify-email-change'
                        ];
                        if (username && !username.includes('/') && username.length > 0 && !reservedPaths.includes(username)) {
                            console.log('🎯 No session - potential affiliate landing page:', username);
                            setCurrentSection('affiliate-landing');
                            setAffiliateUsername(username);
                        }
                    }
                }
            } catch (error) {
                console.error('❌ Session check error:', error);
            } finally {
                setIsCheckingSession(false);
            }
        };

        checkSession();

        // Restore dashboard preference
        // If URL has a shareholder-specific section param, force shareholder dashboard
        const urlParams = new URLSearchParams(window.location.search);
        const sectionParam = urlParams.get('section');
        const shareholderSections = ['overview', 'portfolio', 'shares', 'purchase-shares', 'kyc', ...(DIVIDENDS_USER_VISIBLE ? ['dividends'] : []), 'notifications', 'messages', 'legal-documents', 'settings', 'gold-diggers', 'vein-accord', 'zimbabwe-awaits'];
        if (sectionParam && shareholderSections.includes(sectionParam)) {
            setActiveDashboard('shareholder');
            localStorage.setItem('aureus_active_dashboard', 'shareholder');
        } else {
            const savedDashboard = localStorage.getItem('aureus_active_dashboard') as 'shareholder' | 'affiliate';
            if (savedDashboard && (savedDashboard === 'shareholder' || savedDashboard === 'affiliate')) {
                setActiveDashboard(savedDashboard);
            }
        }
    }, []);

    // Check for admin parameter on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('admin') === 'true') {
            setShowAdmin(true);
        }
    }, []);

    // SECURITY: Email processing moved to server-side (api/cron/process-email-queue.js)
    // Client-side processing has been removed to prevent PII exposure.
    // The admin panel triggers processing via the server-side endpoint.

    // Handle back to main site
    const handleBackToMain = () => {
        setShowAdmin(false);
        // Remove admin parameter from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('admin');
        window.history.replaceState({}, '', url.toString());
    };

    // Maintenance mode: Only block LOGIN and USER DASHBOARD.
    // Home page, public pages, and admin are NOT affected.
    const urlParams = new URLSearchParams(window.location.search);
    const isAdminUrl = urlParams.get('admin') === 'true';
    const isExemptUser = isAdminUser;

    // Sections that maintenance mode blocks (login/register and user dashboard only)
    const isMaintenanceBlockedSection = currentSection === 'account-access' || currentSection === 'dashboard';
    if (maintenanceStatus.isActive && isMaintenanceBlockedSection && !showAdmin && !isAdminUrl && !isExemptUser && (currentSection as string) !== 'test-login') {
        // Show maintenance page only for login and user dashboard
        return <MaintenanceModePage message={maintenanceStatus.message} endsAt={maintenanceStatus.endsAt ?? undefined} />;
    }

    // Show loading screen while checking session
    if (isCheckingSession) {
        return (
            <>
                <NewsTickerBar />
                {maintenanceStatus.isWarning && (
                    <MaintenanceWarningBanner
                        message={maintenanceStatus.message}
                        startsAt={maintenanceStatus.warningStartsAt!}
                        onMaintenanceStart={() => {
                            // Force logout when maintenance starts
                            handleLogout();
                        }}
                    />
                )}
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white min-h-screen flex items-center justify-center" style={{ paddingTop: baseTopOffset }}>
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                        <p className="text-gray-300">Checking session...</p>
                    </div>
                </div>
            </>
        );
    }

    // Show admin interface if admin=true
    if (showAdmin) {
        return (
            <>
                <NewsTickerBar />
                {maintenanceStatus.isWarning && (
                    <MaintenanceWarningBanner
                        message={maintenanceStatus.message}
                        startsAt={maintenanceStatus.warningStartsAt!}
                        onMaintenanceStart={() => {
                            // Admin can see warning but won't be logged out
                            alert('⚠️ Maintenance mode is now active. Regular users have been logged out.');
                        }}
                    />
                )}
                <div style={{ paddingTop: maintenanceStatus.isWarning ? warningTopOffset : baseTopOffset }}>
                    <AdminRouter onBackToMain={handleBackToMain} />
                </div>
            </>
        );
    }

    // v4.7.63 Fraud Freeze: any authenticated user whose database row is
    // marked account_status='suspended' is force-routed to the terminal
    // AccountSuspended screen. This runs BEFORE every other section render
    // so dashboard, wallet, withdrawals, purchases, etc. are all unreachable.
    // The check tolerates either shape (database_user.account_status OR a
    // top-level account_status from older session payloads).
    const __dbUserForSuspensionCheck = user?.database_user || user;
    const __suspensionStatus = __dbUserForSuspensionCheck?.account_status;
    if (user && __suspensionStatus === 'suspended') {
        return (
            <ErrorBoundary>
                <AccountSuspended
                    username={__dbUserForSuspensionCheck?.username || user?.username}
                    suspensionReason={__dbUserForSuspensionCheck?.suspension_reason || null}
                    suspendedAt={__dbUserForSuspensionCheck?.suspended_at || null}
                    onLogout={handleLogout}
                />
            </ErrorBoundary>
        );
    }

    // Show legal pages if selected
    if (currentSection === 'privacy-policy') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div className="legal-section" style={{ paddingTop: baseTopOffset }}>
                    <div className="container">
                        <button
                            className="back-button"
                            onClick={() => {
                                if (user || localStorage.getItem('aureus_user')) {
                                    setCurrentSection('dashboard');
                                    window.history.pushState({}, '', '/dashboard?section=kyc');
                                } else {
                                    setCurrentSection('home');
                                    window.history.pushState({}, '', '/');
                                }
                            }}
                        >
                            {user || localStorage.getItem('aureus_user') ? '← Back to dashboard' : '← Back to Home'}
                        </button>
                        <PrivacyPolicy />
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    if (currentSection === 'terms-conditions') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div className="legal-section" style={{ paddingTop: baseTopOffset }}>
                    <div className="container">
                        <button
                            className="back-button"
                            onClick={() => {
                                if (user || localStorage.getItem('aureus_user')) {
                                    setCurrentSection('dashboard');
                                    window.history.pushState({}, '', '/dashboard?section=kyc');
                                } else {
                                    setCurrentSection('home');
                                    window.history.pushState({}, '', '/');
                                }
                            }}
                        >
                            {user || localStorage.getItem('aureus_user') ? '← Back to dashboard' : '← Back to Home'}
                        </button>
                        <TermsAndConditions />
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    if (currentSection === 'disclaimer') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div className="legal-section" style={{ paddingTop: baseTopOffset }}>
                    <div className="container">
                        <button
                            className="back-button"
                            onClick={() => {
                                if (user || localStorage.getItem('aureus_user')) {
                                    setCurrentSection('dashboard');
                                    window.history.pushState({}, '', '/dashboard?section=kyc');
                                } else {
                                    setCurrentSection('home');
                                    window.history.pushState({}, '', '/');
                                }
                            }}
                        >
                            {user || localStorage.getItem('aureus_user') ? '← Back to dashboard' : '← Back to Home'}
                        </button>
                        <LegalDisclaimer />
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    // CSS TEST MODE - TEMPORARY
    if (window.location.search.includes('csstest')) {
        return (
            <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white min-h-screen overflow-y-auto">
                <div className="container mx-auto px-4 py-8">
                    <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/30 shadow-2xl mb-8">
                        <div className="space-y-8">
                            <div className="text-center">
                                <h3 className="text-2xl font-bold text-white mb-2">
                                    CSS TEST - Sign In to Your Account
                                </h3>
                                <p className="text-gray-400">
                                    Access your Aureus Alliance Holdings dashboard
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent transition-all duration-300"
                                        placeholder="admin@smartunitednetwork.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent transition-all duration-300"
                                        placeholder="••••••••••••••••"
                                    />
                                </div>

                                <button className="w-full bg-gradient-to-r from-gold-400 to-gold-600 text-black font-bold py-3 px-6 rounded-xl hover:from-gold-500 hover:to-gold-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-gold-400/25">
                                    Sign In - CSS TEST
                                </button>

                                <button onClick={() => window.location.search = ''} className="w-full bg-gray-600 text-white font-bold py-2 px-4 rounded-xl hover:bg-gray-700 transition-all duration-300">
                                    Back to App
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Hidden test login page at /testlogin — bypasses maintenance for admin users only
    if (currentSection === 'test-login') {
        return (
            <ErrorBoundary>
                <div style={{ paddingTop: baseTopOffset }}>
                    <UnifiedAuthPage
                        onAuthSuccess={async (userData) => {
                            console.log('🔐 [TEST-LOGIN] Auth success:', userData.database_user?.id);
                            const authUserId = userData.database_user?.id;

                            // Dynamically verify admin status via admin_users table
                            const numId = typeof authUserId === 'number' ? authUserId : parseInt(String(authUserId).replace(/db_/g, ''));
                            const isAdmin = await checkAdminStatus(numId);

                            if (!isAdmin) {
                                // Not an admin — log them out and show maintenance
                                console.warn('🚫 [TEST-LOGIN] Non-admin user attempted test login:', authUserId);
                                alert('Access denied. This login is restricted to authorized administrators during maintenance.');
                                handleLogout();
                                return;
                            }

                            // Admin user — proceed normally
                            setUser(userData);
                            localStorage.setItem('aureus_user', JSON.stringify(userData.database_user));
                            localStorage.setItem('aureus_user_type', userData.user_type || 'shareholder');
                            localStorage.setItem('aureus_auth_success_redirect', 'true');
                            handleDefaultDashboardLogic(userData);
                            setTimeout(() => {
                                localStorage.removeItem('aureus_auth_success_redirect');
                            }, 2000);
                            setPreviousSection('home');
                        }}
                        onBack={() => {
                            setCurrentSection('home');
                            window.history.pushState({}, '', '/');
                        }}
                        {...({ initialMode: "login" } as any)}
                    />
                </div>
            </ErrorBoundary>
        );
    }

    // Show unified account access page for both login and register
    // EXCEPTION: Allow login even during maintenance if ?admin=true is in URL
    if (currentSection === 'account-access') {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        console.log('🔐 Rendering account-access page', { isMobile, authMode, currentSection });

        // Check maintenance mode for login/register pages (unless admin URL or exempt user)
        if (maintenanceStatus.isActive && !isAdminUrl && !isExemptUser) {
            return <MaintenanceModePage message={maintenanceStatus.message} endsAt={maintenanceStatus.endsAt ?? undefined} />;
        }

        return (
            <ErrorBoundary>
                <NewsTickerBar />
                {maintenanceStatus.isWarning && !isAdminUrl && (
                    <MaintenanceWarningBanner
                        message={maintenanceStatus.message}
                        startsAt={maintenanceStatus.warningStartsAt!}
                        onMaintenanceStart={() => {
                            // Force logout when maintenance starts (unless admin URL)
                            handleLogout();
                        }}
                    />
                )}
                <div style={{ paddingTop: maintenanceStatus.isWarning && !isAdminUrl ? warningTopOffset : baseTopOffset }}>
                    <UnifiedAuthPage
                    onAuthSuccess={(userData) => {
                        console.log('✅ Unified auth success:', userData);

                        // Mobile-specific debugging
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        if (isMobile) {
                            console.log('📱 Mobile device - processing auth success callback');
                            console.log('📱 User data received:', {
                                account_type: userData.account_type,
                                user_type: userData.user_type,
                                email: userData.database_user?.email,
                                id: userData.database_user?.id
                            });
                        }

                        setUser(userData);

                        // Store user data in localStorage for persistence
                        localStorage.setItem('aureus_user', JSON.stringify(userData.database_user));

                        // Store user type for proper dashboard routing - default to shareholder
                        localStorage.setItem('aureus_user_type', userData.user_type || 'shareholder');

                        // FIXED: Always redirect to dashboard after login, ignore purchase shares redirect
                        // Users can navigate to purchase shares from the dashboard if needed
                        const postLoginRedirect = localStorage.getItem('aureus_post_login_redirect');
                        if (postLoginRedirect) {
                            console.log('🔄 Post-login redirect found but ignoring:', postLoginRedirect);
                            localStorage.removeItem('aureus_post_login_redirect');
                            console.log('🏠 Redirecting to dashboard instead of purchase shares');
                        }

                        // Always redirect to dashboard after successful authentication
                        console.log('🏠 Redirecting to dashboard after successful auth');
                        console.log('👤 User type:', userData.user_type);

                        // Set a flag to prevent URL routing from interfering
                        localStorage.setItem('aureus_auth_success_redirect', 'true');

                        if (isMobile) {
                            // Check if user is on migration page - if so, skip mobile auth manager
                            const currentPath = window.location.pathname;
                            if (currentPath.includes('migrate')) {
                                console.log('📱 Mobile device on migration page - skipping mobile auth manager and redirects');
                                // Don't redirect migration pages - let them handle their own logic
                                return;
                            } else {
                                console.log('📱 Mobile device detected - using centralized mobile auth manager');
                                mobileAuthManager.startRedirect(userData);
                            }
                        } else {
                            console.log('🖥️ Desktop device - using standard redirect logic');

                            // Use the default dashboard logic for desktop
                            handleDefaultDashboardLogic(userData);

                            // Clear the flag after redirect
                            setTimeout(() => {
                                localStorage.removeItem('aureus_auth_success_redirect');
                            }, 2000);
                        }

                        // Reset previous section
                        setPreviousSection('home');
                    }}
                    onBack={() => {
                        // Go back to where user came from
                        if (previousSection === 'affiliate') {
                            setCurrentSection('affiliate');
                            window.history.pushState({}, '', '/affiliate');
                        } else {
                            setCurrentSection('home');
                            window.history.pushState({}, '', '/');
                        }
                        setPreviousSection('home'); // Reset
                    }}
                    userType={previousSection === 'affiliate' ? 'affiliate' : 'shareholder'}
                />
                </div>
            </ErrorBoundary>
        );
    }

    // Gold Diggers Performance Pool (authenticated users)
    if (currentSection === 'gold-diggers') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div style={{ paddingTop: baseTopOffset }}>
                    <GoldDiggersDashboard
                        user={user}
                        onBack={() => {
                            setCurrentSection('dashboard');
                            window.history.pushState({}, '', '/dashboard');
                        }}
                    />
                </div>
            </ErrorBoundary>
        );
    }

    // Purchase Shares page (authenticated users)
    if (currentSection === 'purchase-shares') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div style={{ paddingTop: baseTopOffset }}>
                    <PurchaseSharesPage
                    onBack={() => {
                        setCurrentSection('dashboard');
                        window.history.pushState({}, '', '/dashboard');
                    }}
                    onNavigate={(page: string) => {
                        setCurrentSection(page);
                        window.history.pushState({}, '', `/${page}`);
                    }}
                    onAuthSuccess={(userData) => {
                        console.log('✅ Purchase shares auth success:', userData);
                        setUser(userData);

                        // Store user data in localStorage for persistence
                        localStorage.setItem('aureus_user', JSON.stringify(userData.database_user));

                        // Store user type as shareholder
                        localStorage.setItem('aureus_user_type', 'shareholder');

                        // Check if user needs profile completion
                        const needsProfile = !userData.database_user?.country_selection_completed ||
                                           !userData.database_user?.full_name ||
                                           !userData.database_user?.phone;

                        if (needsProfile) {
                            console.log('🔄 User needs profile completion, staying on purchase page with profile section');
                            // Store the purchase shares intent for after profile completion
                            localStorage.setItem('aureus_post_profile_redirect', '/purchase-shares');
                            // Don't redirect - let the purchase page handle profile completion
                        } else {
                            // Profile is complete, stay on purchase shares page
                            console.log('✅ Profile complete, staying on purchase shares page');
                            // Clear any redirect since we're already on the purchase page
                            localStorage.removeItem('aureus_post_login_redirect');
                        }
                    }}
                />
                </div>
            </ErrorBoundary>
        );
    }



    // Shareholder registration route (Purchase Shares)
    if (currentSection === 'shareholder-registration') {
        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div style={{ paddingTop: baseTopOffset }}>
                    <PurchaseSharesPage
                    onBack={() => {
                        setCurrentSection('home');
                        window.history.pushState({}, '', '/');
                    }}
                    onNavigate={(page: string) => {
                        setCurrentSection(page);
                        window.history.pushState({}, '', `/${page}`);
                    }}
                    onAuthSuccess={(userData) => {
                        console.log('✅ Purchase shares auth success:', userData);
                        setUser(userData);

                        // Store user data in localStorage for persistence
                        localStorage.setItem('aureus_user', JSON.stringify(userData.database_user));

                        // Store user type as shareholder
                        localStorage.setItem('aureus_user_type', 'shareholder');

                        // Check if user needs profile completion
                        const needsProfile = !userData.database_user?.country_selection_completed ||
                                           !userData.database_user?.full_name ||
                                           !userData.database_user?.phone;

                        if (needsProfile) {
                            console.log('🔄 User needs profile completion, staying on purchase page with profile section');
                            // Store the purchase shares intent for after profile completion
                            localStorage.setItem('aureus_post_profile_redirect', '/purchase-shares');
                            // Don't redirect - let the purchase page handle profile completion
                        } else {
                            // Profile is complete, proceed directly to purchase shares
                            console.log('✅ Profile complete, proceeding to purchase shares');
                            // Clear any redirect since we're already on the purchase page
                            localStorage.removeItem('aureus_post_login_redirect');
                            // Stay on purchase shares page - the PurchaseSharesPage component will handle showing the flow
                        }
                    }}
                />
                </div>
            </ErrorBoundary>
        );
    }

    // Withdrawal Verification Result Page
    if (currentSection === 'withdrawal-verified') {
        return (
            <ErrorBoundary>
                <WithdrawalVerifiedPage />
            </ErrorBoundary>
        );
    }

    // Admin Login Verification Result Page
    if (currentSection === 'admin-login-verified') {
        return (
            <ErrorBoundary>
                <AdminLoginVerifiedPage />
            </ErrorBoundary>
        );
    }

    // Email Change Verification Page
    if (currentSection === 'verify-email-change') {
        return (
            <ErrorBoundary>
                <VerifyEmailChangePage />
            </ErrorBoundary>
        );
    }

    // Username-based affiliate landing page route
    if (currentSection === 'affiliate-landing') {
        console.log('🎯 Rendering username-based affiliate landing page for:', affiliateUsername);

        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div style={{ paddingTop: baseTopOffset }}>
                    <CustomAffiliateLandingPage
                        username={affiliateUsername}
                        onRegistrationSuccess={handleRegistrationSuccess}
                        maintenanceActive={maintenanceStatus.isActive && !isAdminUrl && !isExemptUser}
                    />
                </div>
            </ErrorBoundary>
        );
    }

    // Affiliate landing route
    if (currentSection === 'affiliate') {
        console.log('🏢 Rendering affiliate landing page...');

        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <div style={{ paddingTop: baseTopOffset }}>
                    <AffiliateLandingPage
                    onBackHome={() => { setCurrentSection('home'); window.history.pushState({}, '', '/'); }}
                    onGoToAuth={(mode) => {
                        // v2.9.59: Block registration/login during maintenance
                        if (maintenanceStatus.isActive && !isAdminUrl && !isExemptUser) {
                            setCurrentSection('account-access'); // Will hit the maintenance block
                            return;
                        }
                        setPreviousSection('affiliate');
                        setAuthMode(mode);
                        setCurrentSection('account-access');
                        window.history.pushState({}, '', mode === 'register' ? '/register' : '/login');
                    }}
                    onLogoutAndRegister={async () => {
                        // v2.9.59: Block registration during maintenance
                        if (maintenanceStatus.isActive && !isAdminUrl && !isExemptUser) {
                            setCurrentSection('account-access'); // Will hit the maintenance block
                            return;
                        }
                        console.log('🔄 Logo clicked - logging out and redirecting to registration');

                        if (user) {
                            console.log('👤 User is logged in, logging out...');
                            await handleLogout();
                            console.log('🔄 Force refreshing page for clean state...');
                            window.location.href = '/register';
                            return;
                        }

                        setPreviousSection('affiliate');
                        setAuthMode('register');
                        setCurrentSection('account-access');
                        window.history.pushState({}, '', '/register');
                    }}
                />
                </div>
            </ErrorBoundary>
        );
    }

    // REMOVED: Profile completion section - now handled within dashboard legal documents
    // REMOVED (v7.3.352): Migration & legacy /kyc-certification render block. The /migrate
    // route was decommissioned with the Telegram migration; live KYC is rendered via
    // KYCCenter inside the dashboard. The strings "migrate" and "kyc-certification"
    // remain in reservedPaths above to prevent affiliate-username collisions.

    // Share Audit page (restricted to admin users verified via admin_users table)
    if (currentSection === 'share-audit' && user) {
        if (isAdminUser) {
            return (
                <ErrorBoundary>
                    <ShareAuditPage
                        onBack={() => {
                            setCurrentSection('dashboard');
                            window.history.pushState({}, '', '/dashboard');
                        }}
                        {...({ currentUserId: (user as any)?.id ?? 0 } as any)}
                    />
                </ErrorBoundary>
            );
        } else {
            setCurrentSection('dashboard');
            window.history.pushState({}, '', '/dashboard');
        }
    }

    // Show dashboard if user is logged in and dashboard is selected
    if (currentSection === 'dashboard' && user) {
        // DISABLED: Old profile completion system - now handled by new onboarding system
        // Route guard: Check if user needs basic profile completion
        // const needsBasicProfile = !user.database_user?.country_selection_completed ||
        //                          !user.database_user?.full_name ||
        //                          !user.database_user?.phone;

        // if (needsBasicProfile) {
        //     console.log('🔄 Dashboard access blocked - user needs basic profile completion');
        //     setCurrentSection('basic-profile-completion');
        //     window.history.pushState({}, '', '/complete-profile');
        //     return null;
        // }

        // Route guard: Prevent dashboard access if profile completion is needed
        console.log('🔍 Dashboard route guard check:', {
            needsProfileCompletion: user.needsProfileCompletion,
            profile_completion_required: user.user_metadata?.profile_completion_required,
            currentSection,
            userEmail: user.email,
            hasDatabase: !!user.database_user,
            databaseUserComplete: user.database_user ? {
                hasEmail: !!user.database_user.email,
                hasPassword: !!user.database_user.password_hash,
                hasFullName: !!user.database_user.full_name,
                hasPhone: !!user.database_user.phone,
                hasCountry: !!user.database_user.country_of_residence,
                countrySelectionCompleted: !!user.database_user.country_selection_completed
            } : null
        });

        console.log('✅ Dashboard access granted - user authenticated');

        return (
            <ErrorBoundary>
                <NewsTickerBar />
                <PreferredLanguagePrompt />
                <AnnouncementPopup audience={activeDashboard === 'affiliate' ? 'affiliate' : 'shareholder'} />
                <div style={{ paddingTop: baseTopOffset }}>
                    {activeDashboard === 'affiliate' ? (
                        <AffiliateDashboard
                            user={user}
                            onLogout={handleLogout}
                            onSwitchDashboard={handleDashboardSwitch}
                        />
                    ) : (
                        <UserDashboard
                            user={user}
                            onLogout={handleLogout}
                            onNavigate={setCurrentSection}
                            onSwitchDashboard={handleDashboardSwitch}
                        />
                    )}
                </div>
            </ErrorBoundary>
        );
    }

    // Show the new professional corporate homepage
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('🏠 Rendering homepage (fallback)', { currentSection, isMobile, user: !!user });

    return (
        <ErrorBoundary>
            <NewsTickerBar />
            <div style={{ paddingTop: baseTopOffset }}>
                <CorporateHomepage />
            </div>
        </ErrorBoundary>
    );
};

export default App;

